import type { ReactNode } from "react";

/**
 * Minimal, safe markdown subset for container descriptions (ticket 23).
 *
 * Sanitized by construction: the renderer only ever emits React string
 * children (auto-escaped) — raw HTML in the source is never parsed, and
 * links only allow http/https targets. Supported: headings, paragraphs,
 * unordered/ordered lists, blockquotes, bold, italic, inline code, links.
 */

type Inline =
  | { kind: "text"; text: string }
  | { kind: "strong"; children: Inline[] }
  | { kind: "em"; children: Inline[] }
  | { kind: "code"; text: string }
  | { kind: "link"; href: string; children: Inline[] };

const INLINE_RE =
  /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))/g;

/** Split a line into inline tokens (strong/em/code/link) and plain text. */
export function parseInline(text: string): Inline[] {
  const nodes: Inline[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(INLINE_RE)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push({ kind: "text", text: text.slice(lastIndex, index) });
    const [, strong, strongText, em, emText, code, codeText, link, linkText, href] = match;
    if (strong !== undefined && strongText !== undefined) {
      nodes.push({ kind: "strong", children: [{ kind: "text", text: strongText }] });
    } else if (em !== undefined && emText !== undefined) {
      nodes.push({ kind: "em", children: [{ kind: "text", text: emText }] });
    } else if (code !== undefined && codeText !== undefined) {
      nodes.push({ kind: "code", text: codeText });
    } else if (link !== undefined && linkText !== undefined && href !== undefined && /^https?:\/\//.test(href)) {
      nodes.push({ kind: "link", href, children: [{ kind: "text", text: linkText }] });
    } else if (link !== undefined && linkText !== undefined) {
      // Non-http links are dropped entirely — rendered as plain text.
      nodes.push({ kind: "text", text: linkText });
    }
    lastIndex = match.index! + match[0].length;
  }
  if (lastIndex < text.length) nodes.push({ kind: "text", text: text.slice(lastIndex) });
  return nodes;
}

type Block =
  | { kind: "heading"; level: number; inline: Inline[] }
  | { kind: "paragraph"; inline: Inline[] }
  | { kind: "list"; ordered: boolean; items: Inline[][] }
  | { kind: "quote"; inline: Inline[] };

/** Group lines into blocks; blank lines separate paragraphs. */
export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: Inline[][] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", inline: parseInline(paragraph.join(" ")) });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ kind: "list", ordered: list.ordered, items: list.items });
      list = null;
    }
  };

  for (const rawLine of text.split("\n")) {
    const line = rawLine.trimEnd();
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    const orderedItem = line.match(/^\s*\d+\.\s+(.*)$/);
    const bulletItem = line.match(/^\s*[-*]\s+(.*)$/);
    const quote = line.match(/^>\s?(.*)$/);

    if (line.trim() === "") {
      flushParagraph();
      flushList();
      continue;
    }
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "heading", level: heading[1]!.length, inline: parseInline(heading[2]!) });
      continue;
    }
    if (orderedItem) {
      flushParagraph();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(parseInline(orderedItem[1]!));
      continue;
    }
    if (bulletItem) {
      flushParagraph();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(parseInline(bulletItem[1]!));
      continue;
    }
    if (quote) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "quote", inline: parseInline(quote[1]!) });
      continue;
    }
    if (list) {
      flushList();
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

function renderInline(inline: Inline[]): ReactNode[] {
  return inline.map((node, i) => {
    switch (node.kind) {
      case "text":
        return node.text;
      case "strong":
        return <strong key={i}>{renderInline(node.children)}</strong>;
      case "em":
        return <em key={i}>{renderInline(node.children)}</em>;
      case "code":
        return <code key={i}>{node.text}</code>;
      case "link":
        return (
          <a key={i} href={node.href} target="_blank" rel="noreferrer">
            {renderInline(node.children)}
          </a>
        );
    }
  });
}

function renderBlock(block: Block, key: number): ReactNode {
  switch (block.kind) {
    case "heading": {
      const children = renderInline(block.inline);
      return block.level === 1 ? <h3 key={key}>{children}</h3>
        : block.level === 2 ? <h4 key={key}>{children}</h4>
        : <h5 key={key}>{children}</h5>;
    }
    case "paragraph":
      return <p key={key}>{renderInline(block.inline)}</p>;
    case "list": {
      const items = block.items.map((item, i) => <li key={i}>{renderInline(item)}</li>);
      return block.ordered
        ? <ol key={key}>{items}</ol>
        : <ul key={key}>{items}</ul>;
    }
    case "quote":
      return <blockquote key={key}>{renderInline(block.inline)}</blockquote>;
  }
}

/** Render a description as sanitized markdown (ticket 23). */
export function renderMarkdown(text: string): ReactNode {
  if (!text.trim()) return null;
  return <>{parseBlocks(text).map((block, i) => renderBlock(block, i))}</>;
}